/**
 * The WistiaEvents class with Wistia events captured
 * This class have a responsability of save progress course in lessons
 * @class WistiaEvents
 *
 */

import { services } from "./services";
import { requestGraphQL } from "../../utils/requests";
import { readAmplitudeProps, trackAmplitudeEvents } from "../../utils/events/amplitude";
import { debounce } from "../../utils/events/debounce";

// Set if browser of user is Chrome
const isChrome = !!window.chrome && (!!window.chrome.webstore || !!window.chrome.runtime);
class WistiaEvents {
  constructor() {
    this.countUnLoads = 0;
    this.lastEventEmitted = 0;
    this.lastProgressEmitted = 0;
    this.data = {
      videoItemId: null,
      progress: null,
      eventType: "",
    };
    this.video = null;
  }

  // Set data
  setDataValues(video, eventType) {
    this.data.progress = video.time().toFixed(0);
    this.data.videoItemId = video.container.dataset.videoItemId;
    this.data.eventType = eventType;
  }

  // Send progress
  sendProgress(eventTimer) {
    // Get moment at emitter listener
    const now = eventTimer || new Date().getTime();
    // If time is not duplicated or progress is not duplicated, send Progress
    if (
      this.lastEventEmitted !== now &&
      now > this.lastEventEmitted + 500 &&
      this.data.progress !== this.lastProgressEmitted
    ) {
      this.lastEventEmitted = now;
      this.lastProgressEmitted = this.data.progress;
      // Set new date event emitted and do the request
      requestGraphQL(services.setProgress(this.data));
    }
    return false;
  }

  // submit data
  onSubmit(video, eventType, eventTimer) {
    this.setDataValues(video, eventType);
    this.sendProgress(eventTimer);
  }

  trackEvent(eventName, customProperties) {
    let { eventProperties } = readAmplitudeProps(this.video.container.dataset.amplitudeProps);
    eventProperties = {
      ...eventProperties,
      subtitles_language: this.video.options.wistiaAudio.captionsLanguage,
      video_total_duration_sec: this.video.data.media.duration,
      ...customProperties,
    };
    trackAmplitudeEvents(eventName, eventProperties);
  }

  captureEvents(video) {
    const { container } = video;
    const progressBarMouseUplistener = () => {
      this.trackEvent("video_seekbar_clicked_dragged");
    };

    const nextLessonThumbnailClickListener = () => {
      this.trackEvent("video_next_icon_clicked", { next_component: "thumbnail" });
    };

    const nextLessonLinkClickListener = () => {
      this.trackEvent("video_next_icon_clicked", { next_component: "button" });
    };

    const skipButtonForwardClickListener = () => {
      this.trackEvent("video_forward_clicked");
    };

    const skipButtonBackwardClickListener = () => {
      this.trackEvent("video_player_rewind_clicked");
    };

    const captionSelectedChangeListener = (e) => {
      this.trackEvent("video_subtitles_clicked", { subtitle_selector: e.detail });
    };

    const audioSelectedChangeListener = (e) => {
      this.trackEvent("video_audio_clicked", { audiotrack_selector: e.detail });
    };

    const pipEnterListener = () => {
      this.trackEvent("video_pictureinpicture_clicked", { pictureinpicture_action: "open" });
    };

    const pipLeaveListener = () => {
      this.trackEvent("video_pictureinpicture_clicked", { pictureinpicture_action: "close" });
    };

    const closeCaptionsSearchListener = () => {
      this.trackEvent("video_subtitles_search_clicked", { video_subtitles_search_action: "close" });
    };

    const subtitlesSearchListener = () => {
      this.trackEvent("video_subtitles_search_clicked", { video_subtitles_search_action: "open" });

      // waits 500ms for the closeCaptionsSearch element to be available in the DOM
      setTimeout(() => {
        const closeCaptionsSearch = document.querySelector(
          '[aria-label="Close Interactive Captions"]'
        );

        closeCaptionsSearch.addEventListener("click", closeCaptionsSearchListener);
      }, 500);
    };

    const debouncedQualityChangeListener = debounce(
      (value) => this.trackEvent("video_quality_clicked", { quality_selector: value }),
      500
    );

    video.bind("play", (e) => {
      // Set progress bar element
      const progressBarButton = container.querySelector(".w-bottom-bar-middle");
      // Set skip elements
      const skipButtons = container.querySelectorAll(".js-wistia-skip-button");
      // Set init video id played
      this.data.videoItemId = video.container.dataset.videoItemId;
      // Set date event emitted
      this.lastEventEmitted = e.issuedAt;
      // Add Event Listener in Progress Bar for Jumpeds switch desk or device
      if (progressBarButton) {
        if ("ontouchstart" in document.documentElement) {
          // Touchend in devices
          progressBarButton.addEventListener("touchend", () => this.onSubmit(video, "jumpedTouch"));
          progressBarButton.addEventListener("touchend", progressBarMouseUplistener);
        } else {
          progressBarButton.addEventListener("mouseup", () => this.onSubmit(video, "jumpedMouse"));
          progressBarButton.addEventListener("mouseup", progressBarMouseUplistener);
        }
      }
      // Add Event Listener in Skip Buttons
      if (skipButtons) {
        skipButtons.forEach((element) => {
          element.addEventListener("click", () => this.onSubmit(video, "skipButton"));

          if (element.classList.contains("skip-button--forward")) {
            element.addEventListener("click", skipButtonForwardClickListener);
          }

          if (element.classList.contains("skip-button--backward")) {
            element.addEventListener("click", skipButtonBackwardClickListener);
          }
        });

        const forwardButtonMobile = container.querySelector("div[data-handle='skipAhead']");
        if (forwardButtonMobile) {
          forwardButtonMobile.addEventListener("click", skipButtonForwardClickListener);
        }

        const backwardButtonMobile = container.querySelector("div[data-handle='skipBack']");
        if (backwardButtonMobile) {
          backwardButtonMobile.addEventListener("click", skipButtonBackwardClickListener);
        }
      }

      const nextLessonThumbnail = container.querySelector(".wistia-next-lesson-cta");
      if (nextLessonThumbnail) {
        nextLessonThumbnail.addEventListener("click", nextLessonThumbnailClickListener);
      }

      const nextButtonLink = container.querySelector("div[data-handle='nextButtonLink']");
      if (nextButtonLink) {
        nextButtonLink.addEventListener("click", nextLessonLinkClickListener);
      }

      const audioControl = container.querySelector(".js-audio-control-button-root");
      audioControl.addEventListener("captionselected", captionSelectedChangeListener);
      audioControl.addEventListener("audioselected", audioSelectedChangeListener);

      const pipVideoElementsList = container.querySelectorAll("video");
      pipVideoElementsList.forEach((videoElement) => {
        videoElement.addEventListener("enterpictureinpicture", pipEnterListener);
        videoElement.addEventListener("leavepictureinpicture", pipLeaveListener);
      });

      const captionsControl = container.querySelector('div[data-handle="captions"]');
      captionsControl.addEventListener("click", subtitlesSearchListener);

      let defaultVideoQuality = this.video.videoQuality();
      this.video.bind("qualitychange", function qualityChange(qualityValue) {
        // avoid sending tracking on initial video play
        if (qualityValue !== defaultVideoQuality) {
          debouncedQualityChangeListener(qualityValue);
          defaultVideoQuality = null;
        }
      });
    });

    // Pause video Event
    video.bind("pause", (e) => {
      this.onSubmit(video, "pause", e.issuedAt);
    });

    // End video Event
    video.bind("end", () => {
      this.onSubmit(video, "end");
    });

    const debouncedVolumeChangeTrack = debounce(
      () => this.trackEvent("video_volume_slider_clicked"),
      1500
    );

    video.bind("volumechange", () => {
      debouncedVolumeChangeTrack();
    });

    const debouncedPlaybackRateChangeTrack = debounce(
      (playbackRate) => this.trackEvent("video_speed_clicked", { speed_selector: playbackRate }),
      250
    );

    // With this timer we avoid to track the amplitude event when the user has just played the video
    setTimeout(() => {
      video.bind("playbackratechange", (playbackRate) => {
        debouncedPlaybackRateChangeTrack(playbackRate);
      });
    }, 500);

    video.bind("enterfullscreen", () => {
      this.trackEvent("video_fullscreen_clicked", { fullscreen_action: "open" });
    });

    video.bind("cancelfullscreen", () => {
      this.trackEvent("video_fullscreen_clicked", { fullscreen_action: "close" });
    });

    // BeforeUnload windows Event (Refresh page, change of route) (only works in Chrome)
    if (isChrome) {
      window.addEventListener("beforeunload", (event) => {
        if (!this.countUnLoads) {
          event.preventDefault();
          this.onSubmit(video, "beforeunload");
        }
        this.countUnLoads += 1;
      });
    }
  }

  init(wistiaEmbed) {
    this.video = wistiaEmbed;
    this.captureEvents(wistiaEmbed);
  }
}

Domestika.wistiaEvents = new WistiaEvents();
